import config from '../../config';
import { getAuthHeaders, handleResponse, handleNoContentResponse } from './common';

const invoiceApi = {
    // Provider invoice endpoints
    getInvoices: async (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.status) params.append('status', filters.status);
        if (filters.clientReferenceId) params.append('clientReferenceId', filters.clientReferenceId);
        if (filters.fromDate) params.append('fromDate', filters.fromDate);
        if (filters.toDate) params.append('toDate', filters.toDate);
        if (filters.openOnly) params.append('openOnly', 'true');
        if (filters.search) params.append('search', filters.search);
        if (filters.page) params.append('page', filters.page);
        if (filters.pageSize) params.append('pageSize', filters.pageSize);
        if (filters.includePageInfo) params.append('includePageInfo', 'true');
        const query = params.toString();
        const res = await fetch(`${config.api.url}/invoices${query ? `?${query}` : ''}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    getClientInvoices: async (clientReferenceId, filters = {}) => {
        const params = new URLSearchParams();
        if (filters.status) params.append('status', filters.status);
        if (filters.fromDate) params.append('fromDate', filters.fromDate);
        if (filters.toDate) params.append('toDate', filters.toDate);
        if (filters.page) params.append('page', filters.page);
        if (filters.pageSize) params.append('pageSize', filters.pageSize);
        const query = params.toString();
        const res = await fetch(`${config.api.url}/clients/${clientReferenceId}/invoices${query ? `?${query}` : ''}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    getInvoice: async (referenceId) => {
        const res = await fetch(`${config.api.url}/invoices/${referenceId}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    createInvoice: async (invoiceData) => {
        const res = await fetch(`${config.api.url}/invoices`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(invoiceData)
        });
        return handleResponse(res);
    },

    updateInvoice: async (referenceId, invoiceData) => {
        const res = await fetch(`${config.api.url}/invoices/${referenceId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(invoiceData)
        });
        return handleNoContentResponse(res);
    },

    updateInternalNotes: async (referenceId, internalNotes) => {
        const res = await fetch(`${config.api.url}/invoices/${referenceId}/internal-notes`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ internalNotes })
        });
        return handleResponse(res);
    },

    sendInvoice: async (referenceId) => {
        const res = await fetch(`${config.api.url}/invoices/${referenceId}/send`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    issueInvoice: async (referenceId) => {
        const res = await fetch(`${config.api.url}/invoices/${referenceId}/issue`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    voidInvoice: async (referenceId) => {
        const res = await fetch(`${config.api.url}/invoices/${referenceId}/void`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    deleteInvoice: async (referenceId) => {
        const res = await fetch(`${config.api.url}/invoices/${referenceId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    addInvoiceLineItems: async (invoiceReferenceId, payload) => {
        const res = await fetch(`${config.api.url}/invoices/${invoiceReferenceId}/line-items`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        return handleResponse(res);
    },

    getInvoiceAmendments: async (invoiceReferenceId) => {
        const res = await fetch(`${config.api.url}/invoices/${invoiceReferenceId}/amendments`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    recordPayment: async (referenceId, paymentData) => {
        const res = await fetch(`${config.api.url}/invoices/${referenceId}/payments`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(paymentData)
        });
        return handleResponse(res);
    },

    updatePayment: async (invoiceReferenceId, paymentReferenceId, paymentData) => {
        const res = await fetch(`${config.api.url}/invoices/${invoiceReferenceId}/payments/${paymentReferenceId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(paymentData)
        });
        return handleNoContentResponse(res);
    },

    deletePayment: async (invoiceReferenceId, paymentReferenceId) => {
        const res = await fetch(`${config.api.url}/invoices/${invoiceReferenceId}/payments/${paymentReferenceId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    reconcileStripePayment: async (invoiceReferenceId, paymentReferenceId) => {
        const res = await fetch(`${config.api.url}/invoices/${invoiceReferenceId}/payments/${paymentReferenceId}/reconcile`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    recordRefund: async (invoiceReferenceId, refundData) => {
        const res = await fetch(`${config.api.url}/invoices/${invoiceReferenceId}/refunds`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(refundData)
        });
        return handleResponse(res);
    },

    getRefunds: async (invoiceReferenceId) => {
        const res = await fetch(`${config.api.url}/invoices/${invoiceReferenceId}/refunds`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    updateRefund: async (invoiceReferenceId, refundReferenceId, refundData) => {
        const res = await fetch(`${config.api.url}/invoices/${invoiceReferenceId}/refunds/${refundReferenceId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(refundData)
        });
        return handleNoContentResponse(res);
    },

    deleteRefund: async (invoiceReferenceId, refundReferenceId) => {
        const res = await fetch(`${config.api.url}/invoices/${invoiceReferenceId}/refunds/${refundReferenceId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    // Payment plan endpoints
    getPaymentPlans: async () => {
        const res = await fetch(`${config.api.url}/invoices/payment-plans`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    createPaymentPlan: async (planData) => {
        const res = await fetch(`${config.api.url}/invoices/payment-plans`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(planData)
        });
        return handleResponse(res);
    },

    getPaymentPlan: async (referenceId) => {
        const res = await fetch(`${config.api.url}/invoices/payment-plans/${referenceId}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    cancelPaymentPlan: async (referenceId) => {
        const res = await fetch(`${config.api.url}/invoices/payment-plans/${referenceId}/cancel`, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    deletePaymentPlan: async (referenceId) => {
        const res = await fetch(`${config.api.url}/invoices/payment-plans/${referenceId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    updatePaymentPlan: async (referenceId, planData) => {
        const res = await fetch(`${config.api.url}/invoices/payment-plans/${referenceId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(planData)
        });
        return handleResponse(res);
    },

    // Provider payment settings — auto-syncs Stripe Connect status from Stripe into the DB
    getPaymentSettings: async () => {
        const res = await fetch(`${config.api.url}/invoices/payment-settings`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    getSavedCharges: async () => {
        const res = await fetch(`${config.api.url}/invoices/saved-charges`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    createSavedCharge: async (chargeData) => {
        const res = await fetch(`${config.api.url}/invoices/saved-charges`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(chargeData)
        });
        return handleResponse(res);
    },

    importSavedCharges: async (charges) => {
        const res = await fetch(`${config.api.url}/invoices/saved-charges/import`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ charges })
        });
        return handleResponse(res);
    },

    updateSavedCharge: async (referenceId, chargeData) => {
        const res = await fetch(`${config.api.url}/invoices/saved-charges/${referenceId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(chargeData)
        });
        return handleResponse(res);
    },

    deleteSavedCharge: async (referenceId) => {
        const res = await fetch(`${config.api.url}/invoices/saved-charges/${referenceId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return handleNoContentResponse(res);
    },

    // Client invoice endpoints
    getMyInvoices: async () => {
        const res = await fetch(`${config.api.url}/invoices/my-invoices`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    getMyInvoice: async (referenceId) => {
        const res = await fetch(`${config.api.url}/invoices/my-invoices/${referenceId}`, {
            headers: getAuthHeaders()
        });
        return handleResponse(res);
    },

    getMyInvoicePaymentQuote: async (referenceId, amount = null) => {
        const query = amount != null ? `?amount=${encodeURIComponent(String(amount))}` : '';
        const res = await fetch(
            `${config.api.url}/invoices/my-invoices/${referenceId}/payment-quote${query}`,
            { headers: getAuthHeaders() },
        );
        if (res.status === 404) {
            return { unavailable: true };
        }
        const quote = await handleResponse(res);
        return { unavailable: false, quote };
    },

    payInvoice: async (referenceId, amount = null) => {
        const res = await fetch(`${config.api.url}/invoices/my-invoices/${referenceId}/pay`, {
            method: 'POST',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: amount != null ? JSON.stringify({ amount }) : undefined,
        });
        return handleResponse(res);
    },

    confirmPayment: async (referenceId, paymentIntentId) => {
        const res = await fetch(`${config.api.url}/invoices/my-invoices/${referenceId}/confirm-payment`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ paymentIntentId })
        });
        return handleResponse(res);
    }
};

export default invoiceApi;
